import {
  CustomDutyDetailsDTO,
  ZonosLandedCostLineDTO,
  ZonosLandedCostResponseDTO
} from "@/model/data.model";

// Presentation model for the "landed cost — full breakdown" tooltip (task 4492435474).
// Mirrors the endpoint's own 3 groups (duties / fees / taxes) so nothing is regrouped by the FE;
// each group carries the server's own subtotal (from amountSubtotal).
export interface LandedCostRow {
  label: string;
  detail?: string;
  value: number;
  currency?: string;
}

export type LandedCostCategoryKey = "duty" | "fees" | "vat";

export interface LandedCostCategory {
  key: LandedCostCategoryKey;
  titleKey: string;
  total?: number;
  rows: LandedCostRow[];
  note?: string;
}

const CATEGORY_TITLE_KEY: Record<LandedCostCategoryKey, string> = {
  duty: "custom_duty_duty",
  fees: "landed_cost_group_fees",
  vat: "custom_duty_taxes"
};

function toNumber(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function subtotal(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

// The provider only sends a technical `type` on each line (no note/description here); make it readable.
function humanize(type: string): string {
  return type
    ? type.replace(/[_-]+/g, " ").replace(/^\w/, c => c.toUpperCase())
    : "";
}

// Zonos texts (note/description) are raw English → passthrough; fall back to the humanized type.
function lineLabel(line: ZonosLandedCostLineDTO): string {
  return String(
    line.note ?? line.description ?? humanize(String(line.type ?? ""))
  );
}

// Zonos internal fee types, kept in step with ddp.fee.types.values of the tax-and-duty backend: it sums them into
// amountSubtotal.zonosFee only for DDP MBE EasyDuty, otherwise the summary leaves them out entirely (task 4527024155).
const ZONOS_INTERNAL_FEE_TYPES = [
  "zonos_landed_cost_guarantee",
  "guarantee_percent",
  "guarantee_order",
  "duty_tax_buffer",
  "custom"
];

function isZonosInternalFee(line: ZonosLandedCostLineDTO): boolean {
  return ZONOS_INTERNAL_FEE_TYPES.includes(String(line.type ?? ""));
}

function toCents(value: number): number {
  return Math.round(value * 100) / 100;
}

// True when the summary of the same calculation counts a Zonos fee, i.e. the calculation was DDP MBE EasyDuty.
export function countsZonosFee(
  summary: CustomDutyDetailsDTO | null | undefined
): boolean {
  const netValue = Number(summary?.zonosFee?.netValue);
  return Number.isFinite(netValue) && netValue !== 0;
}

// Store fee lines: the backend forces the amount, so the provider's formula no longer describes it (task 4545901706).
const STORE_FEE_NOTE = "store fee used";

function isStoreFeeLine(line: ZonosLandedCostLineDTO): boolean {
  return (
    String(line.note ?? "")
      .trim()
      .toLowerCase() === STORE_FEE_NOTE
  );
}

function lineDetail(line: ZonosLandedCostLineDTO): string | undefined {
  return line.formula && !isStoreFeeLine(line)
    ? String(line.formula)
    : undefined;
}

// Amounts are expressed in the currency Zonos reports as base (task 4545901706).
export function landedCostBaseCurrency(
  res: ZonosLandedCostResponseDTO | null | undefined
): string | undefined {
  const base = res?.currency?.base;
  return typeof base === "string" && base.trim() ? base.trim() : undefined;
}

export function mapZonosLandedCost(
  res: ZonosLandedCostResponseDTO | null | undefined,
  zonosFeeCounted = true
): LandedCostCategory[] {
  const sub = res?.amountSubtotal ?? {};
  const duties = res?.duties ?? [];
  const taxes = res?.taxes ?? [];
  const allFees = res?.fees ?? [];
  const hidden = (l: ZonosLandedCostLineDTO) =>
    !zonosFeeCounted && isZonosInternalFee(l);
  const hiddenFees = allFees.filter(hidden);
  const fees = allFees.filter(l => !hidden(l));
  const feesSubtotal = subtotal(sub.fees);
  const feesTotal =
    feesSubtotal === undefined || !hiddenFees.length
      ? feesSubtotal
      : toCents(
          hiddenFees.reduce(
            (total, l) => total - toNumber(l.amount),
            feesSubtotal
          )
        );

  const toRows = (lines: ZonosLandedCostLineDTO[]): LandedCostRow[] =>
    lines.map(l => ({
      label: lineLabel(l),
      detail: lineDetail(l),
      value: toNumber(l.amount),
      currency: l.currency
    }));

  const vatCategory: LandedCostCategory = {
    key: "vat",
    titleKey: CATEGORY_TITLE_KEY.vat,
    total: subtotal(sub.taxes),
    rows: toRows(taxes)
  };
  // When taxes[] is empty, surface the de-minimis note (why no VAT was collected) instead of an empty group.
  const deMinimis = res?.deMinimis ?? res?.de_minimis;
  if (!vatCategory.rows.length && deMinimis) {
    vatCategory.note = lineLabel(deMinimis as ZonosLandedCostLineDTO);
  }

  return [
    {
      key: "duty",
      titleKey: CATEGORY_TITLE_KEY.duty,
      total: subtotal(sub.duties),
      rows: toRows(duties)
    },
    {
      key: "fees",
      titleKey: CATEGORY_TITLE_KEY.fees,
      total: feesTotal,
      rows: toRows(fees)
    },
    vatCategory
  ];
}
